/**
 * BulkUploadConfirmationDialog Component
 * Opens immediately with file rows. Each row progresses: uploading → processing → ready.
 * Handles GCS upload + AI parsing internally per row.
 */

import { Fragment, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle, X, AlertTriangle, Trash2, FileText, Loader2, CreditCard, FileUp, User, Sparkles, Upload, Eye } from 'lucide-react';
import { gcsUploadFile, gcsDeleteFile, gcsGetSignedReadUrl } from '@/lib/gcsStorage';
import { parseAiFlowResponse } from '@/lib/parseAiFlowResponse';
import { matchMember, matchRequirement } from '@/lib/certificateSmartMatch';
import { SimplePDFViewer } from '@/components/SimplePDFViewer';

const DOC_TYPES = {
  certificate: { label: 'Certificate', color: 'text-blue-600', bg: 'bg-blue-50' },
  card: { label: 'ID / Card', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  other: { label: 'Other', color: 'text-gray-500', bg: 'bg-gray-50' },
};

// Row statuses
const STATUS = { uploading: 'uploading', processing: 'processing', ready: 'ready', error: 'error' };

const BulkUploadConfirmationDialog = ({ open, data, isSaving, onConfirm, onCancel }) => {
  const [rows, setRows] = useState([]);
  const [previewRowId, setPreviewRowId] = useState(null);
  const processedRef = useRef(false);
  const abortControllersRef = useRef(new Map());

  const teamMembers = data?.teamMembers || [];
  const requirements = data?.requirements || [];
  const projectId = data?.projectId;

  // Initialize rows from raw files and kick off processing
  useEffect(() => {
    if (!data?.files?.length || processedRef.current) return;
    processedRef.current = true;

    const initialRows = data.files.map((file, idx) => ({
      id: idx,
      file,
      fileName: file.name,
      status: STATUS.uploading,
      tempFilePath: null,
      detectedPerson: null,
      detectedCourse: null,
      memberId: '',
      requirementId: '',
      editableExpiry: '',
      editableCertNumber: '',
      docType: 'certificate',
      attachToRequirementId: '',
      previewUrl: null,
      error: null,
      removed: false,
    }));

    setRows(initialRows);

    // Process each file in parallel
    initialRows.forEach(row => processFile(row));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      processedRef.current = false;
      setRows([]);
      // Abort any lingering requests
      for (const controller of abortControllersRef.current.values()) {
        controller.abort();
      }
      abortControllersRef.current.clear();
    }
  }, [open]);

  const processFile = async (row) => {
    const tm = data?.teamMembers || [];
    const reqs = data?.requirements || [];

    // Create an AbortController for this row
    const controller = new AbortController();
    abortControllersRef.current.set(row.id, controller);
    const { signal } = controller;

    // 1. Upload to GCS
    try {
      const uploadResult = await gcsUploadFile(row.file, {
        folderPath: 'certificates/temp',
        fileName: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}_${row.file.name}`,
        contentType: row.file.type,
        signal,
      });

      // 2. Get signed URL for AI parse + preview
      let signedUrl;
      try {
        signedUrl = await gcsGetSignedReadUrl(uploadResult.filePath);
      } catch {
        if (signal.aborted) return;
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: STATUS.ready, tempFilePath: uploadResult.filePath, error: 'Could not get file URL' } : r));
        return;
      }

      if (signal.aborted) {
        // File was uploaded but cancelled before processing — clean up
        gcsDeleteFile(uploadResult.filePath).catch(() => {});
        return;
      }

      setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: STATUS.processing, tempFilePath: uploadResult.filePath, previewUrl: signedUrl } : r));

      const response = await fetch(
        'https://addai-flows-backend-29522465016.europe-west2.run.app/webhook/e2a909a7-6f57-461d-83b8-b1506cf3a79a/process-certificate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentUrl: signedUrl, fileName: row.file.name, projectId, uploadedAt: new Date().toISOString() }),
          signal,
        }
      );

      if (signal.aborted) return;

      if (!response.ok) {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: STATUS.ready, error: 'AI could not read this file' } : r));
        return;
      }

      const responseData = await response.json();
      const parsed = parseAiFlowResponse(responseData);

      if (signal.aborted) return;

      if (!parsed.success || !parsed.extractedData) {
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: STATUS.ready, error: 'No data could be extracted' } : r));
        return;
      }

      const d = parsed.extractedData;
      const firstName = Array.isArray(d.first_name) ? d.first_name[0] : d.first_name;
      const lastName = Array.isArray(d.last_name) ? d.last_name[0] : d.last_name;
      const detectedPerson = [firstName, lastName].filter(Boolean).join(' ') || null;
      const detectedCourse = (Array.isArray(d.course_title) ? d.course_title[0] : d.course_title) || null;
      const expiryDate = (Array.isArray(d.expiry_date) ? d.expiry_date[0] : d.expiry_date) || '';
      const certNumber = (Array.isArray(d.certificate_number) ? d.certificate_number[0] : d.certificate_number) || '';

      const matched = matchMember(d, tm);
      const matchedReq = matchRequirement(d, reqs);

      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r,
        status: STATUS.ready,
        detectedPerson,
        detectedCourse,
        memberId: matched?.id || '',
        requirementId: matchedReq?.id || '',
        editableExpiry: expiryDate,
        editableCertNumber: certNumber,
        error: (!matched || !matchedReq)
          ? (!matched && !matchedReq ? 'No match for person or qualification'
            : !matched ? `No match for person: ${detectedPerson || 'unknown'}`
            : `No match for qualification: ${detectedCourse || 'unknown'}`)
          : null,
      } : r));

    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) return; // Cancelled — row already removed
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: STATUS.error, error: 'Upload failed' } : r));
    } finally {
      abortControllersRef.current.delete(row.id);
    }
  };

  const isCertRow = r => r.docType === 'certificate';
  const activeRows = rows.filter(r => !r.removed);
  const readyRows = activeRows.filter(r => r.status === STATUS.ready);
  const readyCertCount = readyRows.filter(r => isCertRow(r) && r.memberId && r.requirementId).length;
  const readySupportingCount = readyRows.filter(r => !isCertRow(r) && r.memberId && r.attachToRequirementId).length;
  const supportingCount = activeRows.filter(r => !isCertRow(r)).length;
  const pendingCount = activeRows.filter(r => r.status === STATUS.uploading || r.status === STATUS.processing).length;
  const unmatchedCount = readyRows.filter(r => {
    if (isCertRow(r)) return !r.memberId || !r.requirementId;
    return !r.memberId || !r.attachToRequirementId;
  }).length;

  const previewRow = previewRowId !== null ? rows.find(r => r.id === previewRowId) : null;
  const previewFileType = useMemo(() => {
    const name = previewRow?.fileName;
    if (!name) return 'unknown';
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
    return 'unknown';
  }, [previewRow?.fileName]);

  const updateRow = useCallback((id, updates) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  }, []);

  const removeRow = useCallback((id) => {
    // Abort any in-flight upload/processing for this row
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    setRows(prev => {
      const row = prev.find(r => r.id === id);
      if (row?.tempFilePath) gcsDeleteFile(row.tempFilePath).catch(() => {});
      return prev.map(r => r.id === id ? { ...r, removed: true } : r);
    });
  }, []);

  const getCertOptionsForMember = useCallback((memberId) => {
    if (!memberId) return [];
    return rows
      .filter(r => !r.removed && r.memberId === memberId && isCertRow(r) && r.requirementId && r.status === STATUS.ready)
      .map(r => ({
        requirementId: r.requirementId,
        label: requirements.find(req => req.id === r.requirementId)?.name || r.detectedCourse || 'Unknown'
      }));
  }, [rows, requirements]);

  const handleConfirm = () => {
    // Group cert + supporting by (memberId, requirementId)
    const qualGroups = new Map();
    for (const row of readyRows) {
      if (!row.memberId || !isCertRow(row) || !row.requirementId) continue;
      const key = `${row.memberId}-${row.requirementId}`;
      if (!qualGroups.has(key)) qualGroups.set(key, { certRow: row, supportingRows: [] });
      else qualGroups.get(key).certRow = row;
    }
    for (const row of readyRows) {
      if (isCertRow(row) || !row.memberId || !row.attachToRequirementId) continue;
      const key = `${row.memberId}-${row.attachToRequirementId}`;
      if (qualGroups.has(key)) qualGroups.get(key).supportingRows.push(row);
    }

    const toSave = [];
    for (const group of qualGroups.values()) {
      if (!group.certRow) continue;
      const cert = group.certRow;
      const allFiles = [cert, ...group.supportingRows];
      toSave.push({
        fileName: cert.fileName,
        tempFilePath: cert.tempFilePath,
        memberId: cert.memberId,
        requirementId: cert.requirementId,
        memberName: teamMembers.find(m => m.id === cert.memberId)?.name || '',
        requirementName: requirements.find(r => r.id === cert.requirementId)?.name || '',
        expiryDate: cert.editableExpiry || null,
        certificateNumber: cert.editableCertNumber || null,
        evidenceFiles: allFiles.map(f => ({
          url: f.tempFilePath,
          file_name: f.fileName,
          file_type: 'unknown',
          uploaded_at: new Date().toISOString(),
          uploaded_by: null,
          is_primary: isCertRow(f),
          doc_type: f.docType,
        })),
      });
    }
    onConfirm(toSave);
  };

  const handleCancel = () => {
    // Abort all in-flight requests
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    for (const r of rows) {
      if (r.tempFilePath) gcsDeleteFile(r.tempFilePath).catch(() => {});
    }
    setRows([]);
    onCancel();
  };

  // Group rows by memberId for display
  const groupedRows = useMemo(() => {
    const groups = new Map();
    const unmatched = [];

    rows.forEach((row) => {
      if (row.removed) return;
      if (!row.memberId || row.status !== STATUS.ready) {
        unmatched.push(row);
        return;
      }
      if (!groups.has(row.memberId)) {
        const member = teamMembers.find(m => m.id === row.memberId);
        groups.set(row.memberId, { memberName: member?.name || row.detectedPerson || 'Unknown', items: [] });
      }
      groups.get(row.memberId).items.push(row);
    });

    for (const group of groups.values()) {
      group.items.sort((a, b) => (a.docType === 'certificate' ? -1 : 1) - (b.docType === 'certificate' ? -1 : 1));
    }

    return { groups: Array.from(groups.entries()), unmatched };
  }, [rows, teamMembers]);

  if (!data) return null;

  const renderLoadingRow = (row) => (
    <tr key={row.id} className="bg-gray-50/50">
      <td className="px-3 py-3 w-8">
        <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
      </td>
      <td className="px-3 py-3 w-[120px]">
        <span className="inline-flex items-center gap-1.5 h-7 px-2 rounded text-[11px] font-medium bg-gray-100 text-gray-400">
          {row.status === STATUS.uploading ? (
            <><Upload className="h-3 w-3" />Uploading</>
          ) : (
            <><Sparkles className="h-3 w-3" />Analysing</>
          )}
        </span>
      </td>
      <td className="px-3 py-3" colSpan={5}>
        <span className="text-sm text-gray-500">{row.fileName}</span>
      </td>
      <td className="px-3 py-3 w-16 text-center">
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled title="Preview available when processed">
            <Eye className="h-4 w-4 text-gray-300" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeRow(row.id)}>
            <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
          </Button>
        </div>
      </td>
    </tr>
  );

  const renderReadyRow = (row) => {
    const isCert = isCertRow(row);
    const isSupporting = !isCert;
    const isError = row.status === STATUS.error;
    const isReady = isError ? false : isCert ? (row.memberId && row.requirementId) : (row.memberId && row.attachToRequirementId);
    const typeConfig = DOC_TYPES[row.docType] || DOC_TYPES.certificate;
    const certOptions = isSupporting ? getCertOptionsForMember(row.memberId) : [];

    return (
      <tr
        key={row.id}
        className={isError ? 'bg-red-50/50' : !isReady ? 'bg-amber-50/30' : 'bg-white hover:bg-gray-50/50'}
      >
        <td className="px-3 py-2.5 w-8">
          {isError ? <AlertTriangle className="h-4 w-4 text-red-400" />
            : isReady ? <CheckCircle className="h-4 w-4 text-green-500" />
            : <AlertTriangle className="h-4 w-4 text-amber-500" />}
        </td>
        <td className="px-3 py-2.5 w-[120px]">
          <Select value={row.docType} onValueChange={(val) => updateRow(row.id, { docType: val, attachToRequirementId: '' })}>
            <SelectTrigger className={`h-7 text-[11px] font-medium border-0 px-2 ${typeConfig.bg} ${typeConfig.color} w-full`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certificate" className="text-xs">Certificate</SelectItem>
              <SelectItem value="card" className="text-xs">ID / Card</SelectItem>
              <SelectItem value="other" className="text-xs">Other</SelectItem>
            </SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2.5 min-w-[160px]">
          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={row.fileName}>{row.fileName}</div>
          {(row.detectedPerson || row.detectedCourse) && (
            <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[200px]">
              {row.detectedPerson}{row.detectedPerson && row.detectedCourse ? ' · ' : ''}{row.detectedCourse}
            </div>
          )}
          {isError && <div className="text-xs text-red-500 mt-0.5">{row.error}</div>}
        </td>
        <td className="px-3 py-2.5 min-w-[180px]">
          <Select value={row.memberId} onValueChange={(val) => updateRow(row.id, { memberId: val, attachToRequirementId: '' })}>
            <SelectTrigger className={`h-8 text-xs w-full ${!row.memberId ? 'border-amber-300' : ''}`}>
              <SelectValue placeholder="Select person..." />
            </SelectTrigger>
            <SelectContent>
              {teamMembers.map(m => (
                <SelectItem key={m.id} value={m.id} className="text-xs">
                  {m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2.5 min-w-[200px]">
          {isCert && (
            <Select value={row.requirementId} onValueChange={(val) => updateRow(row.id, { requirementId: val })}>
              <SelectTrigger className={`h-8 text-xs w-full ${!row.requirementId ? 'border-amber-300' : ''}`}>
                <SelectValue placeholder="Select qualification..." />
              </SelectTrigger>
              <SelectContent>
                {requirements.map(r => (
                  <SelectItem key={r.id} value={r.id} className="text-xs">{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isSupporting && (
            certOptions.length > 0 ? (
              <Select value={row.attachToRequirementId || ''} onValueChange={(val) => updateRow(row.id, { attachToRequirementId: val })}>
                <SelectTrigger className={`h-8 text-xs w-full ${!row.attachToRequirementId ? 'border-amber-300' : ''}`}>
                  <SelectValue placeholder="Attach to qualification..." />
                </SelectTrigger>
                <SelectContent>
                  {certOptions.map(opt => (
                    <SelectItem key={opt.requirementId} value={opt.requirementId} className="text-xs">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-[11px] text-gray-400 italic">
                {row.memberId ? 'No certificates for this person yet' : 'Select a person first'}
              </span>
            )
          )}
        </td>
        <td className="px-3 py-2.5 w-[130px]">
          {isCert && (
            <Input type="date" value={row.editableExpiry || ''} onChange={(e) => updateRow(row.id, { editableExpiry: e.target.value })} className="h-8 text-xs w-full" />
          )}
        </td>
        <td className="px-3 py-2.5 w-[130px]">
          {isCert && (
            <Input value={row.editableCertNumber || ''} onChange={(e) => updateRow(row.id, { editableCertNumber: e.target.value })} placeholder="Cert #" className="h-8 text-xs w-full" />
          )}
        </td>
        <td className="px-3 py-2.5 w-16 text-center">
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className={`h-7 w-7 p-0 ${previewRowId === row.id ? 'bg-blue-100 text-blue-600' : ''}`}
              onClick={() => setPreviewRowId(previewRowId === row.id ? null : row.id)}
              disabled={!row.previewUrl}
              title={row.previewUrl ? 'Preview file' : 'No preview available'}
            >
              <Eye className={`h-4 w-4 ${previewRowId === row.id ? 'text-blue-600' : 'text-gray-400 hover:text-blue-500'}`} />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { if (previewRowId === row.id) setPreviewRowId(null); removeRow(row.id); }}>
              <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="!max-w-[92vw] w-[1300px] max-h-[85vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b flex-none">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-blue-600" />
            Review Uploads ({activeRows.length} files)
          </DialogTitle>
          <div className="flex items-center gap-3 text-sm mt-1">
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 text-blue-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />{pendingCount} processing
              </span>
            )}
            {readyCertCount > 0 && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-3.5 w-3.5" />{readyCertCount} certificate{readyCertCount !== 1 ? 's' : ''} ready
              </span>
            )}
            {supportingCount > 0 && (
              <span className="text-gray-500">{supportingCount} supporting</span>
            )}
            {unmatchedCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />{unmatchedCount} need attention
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Table section */}
          <div className="flex-1 overflow-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-50 border-b z-10">
              <tr className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                <th className="px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2 w-[120px]">Type</th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Team Member</th>
                <th className="text-left px-3 py-2">Qualification</th>
                <th className="text-left px-3 py-2 w-[130px]">Expiry</th>
                <th className="text-left px-3 py-2 w-[130px]">Cert #</th>
                <th className="px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {/* Loading rows at top */}
              {groupedRows.unmatched.filter(r => r.status === STATUS.uploading || r.status === STATUS.processing).map(row => renderLoadingRow(row))}

              {/* Grouped ready rows by member */}
              {groupedRows.groups.map(([memberId, group]) => (
                <Fragment key={memberId}>
                  <tr className="bg-gray-50/80 border-t border-gray-200">
                    <td colSpan={8} className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-xs font-semibold text-gray-600">{group.memberName}</span>
                        <span className="text-[10px] text-gray-400">({group.items.length} file{group.items.length !== 1 ? 's' : ''})</span>
                      </div>
                    </td>
                  </tr>
                  {group.items.map(row => renderReadyRow(row))}
                </Fragment>
              ))}

              {/* Unmatched ready rows */}
              {groupedRows.unmatched.filter(r => r.status === STATUS.ready || r.status === STATUS.error).length > 0 && (
                <Fragment>
                  <tr className="bg-amber-50/50 border-t border-amber-200">
                    <td colSpan={8} className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs font-semibold text-amber-700">Unmatched</span>
                        <span className="text-[10px] text-gray-400">— these will not be saved unless matched to a person and qualification</span>
                      </div>
                    </td>
                  </tr>
                  {groupedRows.unmatched.filter(r => r.status === STATUS.ready || r.status === STATUS.error).map(row => renderReadyRow(row))}
                </Fragment>
              )}
            </tbody>
          </table>
          </div>

          {/* Preview pane — slides in from right */}
          {previewRow && (
            <div className="w-1/2 flex-none border-l bg-gray-100 flex flex-col animate-in slide-in-from-right duration-200">
              {/* Preview header */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white flex-none">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-4 w-4 text-gray-500 flex-none" />
                  <span className="text-sm font-medium text-gray-700 truncate">{previewRow.fileName}</span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-none" onClick={() => setPreviewRowId(null)}>
                  <X className="h-4 w-4 text-gray-400" />
                </Button>
              </div>
              {/* Preview content */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {previewRow.previewUrl ? (
                  previewFileType === 'pdf' ? (
                    <SimplePDFViewer url={previewRow.previewUrl} className="h-full" defaultScale={0.5} />
                  ) : previewFileType === 'image' ? (
                    <div className="h-full flex items-center justify-center p-4 bg-neutral-900">
                      <img src={previewRow.previewUrl} alt="Preview" className="max-w-full max-h-full object-contain rounded shadow-lg" />
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                      <FileText className="h-12 w-12 mb-3" />
                      <p className="text-sm">Preview not available</p>
                    </div>
                  )
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin mb-3" />
                    <p className="text-sm">Loading preview...</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t flex-none gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-2" />Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-green-600 hover:bg-green-700"
            disabled={isSaving || readyCertCount === 0 || pendingCount > 0}
          >
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : pendingCount > 0 ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing {pendingCount} file{pendingCount !== 1 ? 's' : ''}...</>
            ) : (
              <><CheckCircle className="h-4 w-4 mr-2" />Save {readyCertCount} Certificate{readyCertCount !== 1 ? 's' : ''}{readySupportingCount > 0 ? ` + ${readySupportingCount} supporting` : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

BulkUploadConfirmationDialog.displayName = 'BulkUploadConfirmationDialog';

export default BulkUploadConfirmationDialog;
